import React, { memo, useCallback, useRef, useEffect, useState } from "react";
import { Box, Avatar, Typography, TextField, InputAdornment, IconButton, Button, CircularProgress, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip } from "@mui/material";
import { Search, MoreVert, Send, Mic, EmojiEmotions, ArrowBack, Schedule, Check, DoneAll, Person, AttachFile, Image, InsertDriveFile, Videocam, PlayArrow, Pause, GetApp, VolumeUp, Close, Stop, Edit, Delete, Reply, Forward, KeyboardArrowDown, Camera, Description, Chat, MusicNote } from "@mui/icons-material";
import { validateFile } from "../../../utils/FileValidation";
import { errorToster } from "../../../shared/toster/Toster";
import { format, isToday, isYesterday } from 'date-fns';
import NameEditDialog from "./NameEditDialog";

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDateHeader = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  
  if (messageDate.getTime() === today.getTime()) return 'Today';
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (messageDate.getTime() === yesterday.getTime()) return 'Yesterday';
  
  return date.toLocaleDateString([], { 
    month: 'short', 
    day: 'numeric', 
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
  });
};

const DateHeader = memo(({ date, isUnread = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
    <Box sx={{ 
      backgroundColor: isUnread ? '#06CF9C' : 'rgba(11, 20, 26, 0.15)', 
      borderRadius: '7px', 
      px: 2, 
      py: 0.5,
      boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)'
    }}>
      <Typography sx={{ 
        fontSize: '12.5px', 
        color: isUnread ? '#FFFFFF' : '#8696A0', 
        fontWeight: 400,
        textAlign: 'center'
      }}>
        {isUnread ? 'UNREAD MESSAGES' : formatDateHeader(date)}
      </Typography>
    </Box>
  </Box>
));

// Delete Confirmation Dialog
const DeleteMessageDialog = memo(({ open, message, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontSize: '18px', fontWeight: 500, color: '#3B4A54' }}>
      Delete message?
    </DialogTitle>
    <DialogContent>
      <Typography sx={{ fontSize: '14px', color: '#667781', mb: 2 }}>
        Are you sure you want to delete this message?
      </Typography>
      <Box sx={{ 
        backgroundColor: '#F7F8FA', 
        p: 2, 
        borderRadius: '8px',
        borderLeft: '4px solid #00A884'
      }}>
        <Typography sx={{ fontSize: '13px', color: '#3B4A54', fontStyle: 'italic' }}>
          "{message?.text?.slice(0, 50)}{message?.text?.length > 50 ? '...' : ''}"
        </Typography>
      </Box>
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <Button onClick={onClose} sx={{ color: '#667781', textTransform: 'none' }}>
        Cancel
      </Button>
      <Button 
        onClick={onConfirm} 
        sx={{ 
          color: '#E53E3E', 
          textTransform: 'none',
          '&:hover': { backgroundColor: 'rgba(229, 62, 62, 0.1)' }
        }}
      >
        Delete
      </Button>
    </DialogActions>
  </Dialog>
));

const MessageBubble = memo(({ msg, onRetry, showRetry, onDelete }) => {
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      const handleLoadedMetadata = () => setAudioDuration(audio.duration);
      const handleTimeUpdate = () => {
        if (audio.duration) {
          setAudioProgress((audio.currentTime / audio.duration) * 100);
          setCurrentTime(audio.currentTime);
        }
      };
      const handleEnded = () => {
        setAudioPlaying(false);
        setAudioProgress(0);
        setCurrentTime(0);
      };
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      return () => {
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
      };
    }
  }, [msg.mediaUrl]);

  const handleAudioPlay = () => {
    if (audioRef.current) {
      if (audioPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setAudioPlaying(!audioPlaying);
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (audio && audioDuration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const ratio = clickX / rect.width;
      audio.currentTime = ratio * audioDuration;
    }
  };

  const handleDeleteConfirm = () => {
    onDelete(msg);
    setShowDeleteDialog(false);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = msg.mediaUrl;
    link.download = msg.filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setMenuAnchor(null);
  };

  const isMediaMessage = msg.messageType !== 'text' && msg.mediaUrl;
  const showTimeOverlay = msg.messageType === 'image' || msg.messageType === 'video';
  const shouldShowCaption = msg.text && !msg.text.startsWith('[');

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: msg.isMe ? 'flex-end' : 'flex-start',
          mb: 0.5,
          px: 1,
          position: 'relative',
          '&:hover .message-options': { opacity: 1 }
        }}
      >
        <Box
          sx={{
            maxWidth: '75%',
            backgroundColor: msg.isMe ? '#D1F4CC' : '#FFFFFF',
            borderRadius: msg.isMe ? '7.5px 7.5px 0 7.5px' : '7.5px 7.5px 7.5px 0',
            overflow: 'hidden',
            boxShadow: '0 1px 0.5px rgba(11, 20, 26, 0.13)',
            position: 'relative'
          }}
        >
          {/* 3-dot menu */}
          <IconButton
            className="message-options"
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              opacity: 0,
              backgroundColor: 'rgba(11,20,26,0.1)',
              transition: 'opacity 0.2s',
              zIndex: 10
            }}
          >
            <MoreVert sx={{ fontSize: 14, color: '#667781' }} />
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {isMediaMessage && (
              <MenuItem onClick={handleDownload}>
                <GetApp sx={{ fontSize: 16, mr: 1 }} />
                Download
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                setShowDeleteDialog(true);
                setMenuAnchor(null);
              }}
            >
              <Delete sx={{ fontSize: 16, mr: 1 }} />
              Delete
            </MenuItem>
          </Menu>

          {/* ==== MEDIA PREVIEW ==== */}
          {isMediaMessage && (
            <Box sx={{ maxWidth: 300, width: '100%' }}>
              {msg.messageType === 'image' && (
                <>
                  <Box sx={{ position: 'relative' }}>
                    <img
                      src={msg.mediaUrl}
                      alt="img"
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '400px',
                        display: 'block',
                        borderRadius: '8px'
                      }}
                    />
                    {showTimeOverlay && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          backgroundColor: 'rgba(11, 20, 26, 0.8)',
                          borderRadius: '12px',
                          px: 1,
                          py: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <Typography sx={{ fontSize: 11, color: '#fff' }}>{msg.time}</Typography>
                        {msg.isMe && (
                          <>
                            {msg.status === 'sending' && <Schedule sx={{ fontSize: 12, color: '#fff' }} />}
                            {msg.status === 'sent' && <Check sx={{ fontSize: 12, color: '#fff' }} />}
                            {msg.status === 'delivered' && <DoneAll sx={{ fontSize: 12, color: '#fff' }} />}
                            {msg.status === 'read' && <DoneAll sx={{ fontSize: 12, color: '#53BDEB' }} />}
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                  {shouldShowCaption && (
                    <Box sx={{ px: 1, pt: 0.5 }}>
                      <Typography sx={{ fontSize: 14, wordBreak: 'break-word' }}>{msg.text}</Typography>
                    </Box>
                  )}
                </>
              )}

              {msg.messageType === 'video' && (
                <>
                  <Box sx={{ position: 'relative' }}>
                    <video
                      controls
                      src={msg.mediaUrl}
                      style={{ width: '100%', borderRadius: 8, maxHeight: 400 }}
                    />
                    {showTimeOverlay && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          backgroundColor: 'rgba(11, 20, 26, 0.8)',
                          borderRadius: '12px',
                          px: 1,
                          py: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <Typography sx={{ fontSize: 11, color: '#fff' }}>{msg.time}</Typography>
                        {msg.isMe && (
                          <>
                            {msg.status === 'sending' && <Schedule sx={{ fontSize: 12, color: '#fff' }} />}
                            {msg.status === 'sent' && <Check sx={{ fontSize: 12, color: '#fff' }} />}
                            {msg.status === 'delivered' && <DoneAll sx={{ fontSize: 12, color: '#fff' }} />}
                            {msg.status === 'read' && <DoneAll sx={{ fontSize: 12, color: '#53BDEB' }} />}
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                  {shouldShowCaption && (
                    <Box sx={{ px: 1, py: 0.5 }}>
                      <Typography sx={{ fontSize: 14, wordBreak: 'break-word' }}>{msg.text}</Typography>
                    </Box>
                  )}
                </>
              )}

             {msg.messageType === 'audio' && (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  p: 1.5, 
                  minWidth: '200px', 
                  maxWidth: '280px', 
                  backgroundColor: msg.isMe ? 'rgba(11, 20, 26, 0.05)' : 'rgba(11, 20, 26, 0.02)' 
                }}>
                  <IconButton 
                    size="small" 
                    onClick={handleAudioPlay} 
                    sx={{ 
                      backgroundColor: '#00A884', 
                      color: '#FFFFFF', 
                      width: 36, 
                      height: 36, 
                      '&:hover': { backgroundColor: '#008F72' } 
                    }}
                  >
                    {audioPlaying ? <Pause sx={{ fontSize: 20 }} /> : <PlayArrow sx={{ fontSize: 20 }} />}
                  </IconButton>
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VolumeUp sx={{ fontSize: '16px', color: '#8696A0' }} />
                    <Box sx={{ 
                      flex: 1, 
                      height: '4px', 
                      backgroundColor: '#E2E8F0', 
                      borderRadius: '2px', 
                      position: 'relative', 
                      cursor: 'pointer' 
                    }} 
                    onClick={handleProgressClick}
                    >
                      <Box sx={{ 
                        width: `${audioProgress}%`, 
                        height: '100%', 
                        backgroundColor: '#00A884', 
                        borderRadius: '2px', 
                        transition: 'width 0.1s ease' 
                      }} />
                    </Box>
                    <Typography sx={{ 
                      fontSize: '12px', 
                      color: '#8696A0', 
                      minWidth: '35px', 
                      textAlign: 'right' 
                    }}>
                      {audioPlaying ? formatTime(Math.floor(currentTime)) : '0:00'}
                    </Typography>
                  </Box>
                  <audio 
                    ref={audioRef} 
                    src={msg.mediaUrl} 
                    preload="metadata" 
                    style={{ display: 'none' }} 
                  />
                </Box>
              )}

              {msg.messageType === 'document' && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5 }}>
                    <Box sx={{ 
                      width: 48, 
                      height: 48, 
                      borderRadius: '50%', 
                      backgroundColor: '#00A884', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Description sx={{ fontSize: '24px', color: '#FFFFFF' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ 
                        fontSize: '14px', 
                        fontWeight: 500, 
                        color: '#00A884', 
                        mb: 0.25, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap' 
                      }}>
                        {msg.filename || 'Document'}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#8696A0' }}>
                        {formatFileSize(msg.mediaSize || 0)}
                      </Typography>
                    </Box>
                    <IconButton size="small" sx={{ color: '#8696A0' }} onClick={handleDownload}>
                      <GetApp />
                    </IconButton>
                  </Box>
                  {shouldShowCaption && (
                    <Box sx={{ px: 1.5, pb: 1 }}>
                      <Typography sx={{ fontSize: 14, wordBreak: 'break-word' }}>{msg.text}</Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {/* === Text Only Message === */}
          {!isMediaMessage && msg.text && (
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography sx={{ fontSize: 14, wordBreak: 'break-word' }}>{msg.text}</Typography>
            </Box>
          )}

          {/* Footer Time + Status */}
          {!showTimeOverlay && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: 1.5,
                pb: 0.5,
                pt: 0,
                gap: 0.5
              }}
            >
              <Typography sx={{ fontSize: 11, color: '#667781' }}>{msg.time}</Typography>
              {msg.isMe && (
                <>
                  {msg.status === 'sending' && <Schedule sx={{ fontSize: 14, color: '#667781' }} />}
                  {msg.status === 'sent' && <Check sx={{ fontSize: 14, color: '#667781' }} />}
                  {msg.status === 'delivered' && <DoneAll sx={{ fontSize: 14, color: '#667781' }} />}
                  {msg.status === 'read' && <DoneAll sx={{ fontSize: 14, color: '#53BDEB' }} />}
                  {msg.status === 'failed' && showRetry && (
                    <IconButton size="small" onClick={() => onRetry(msg.tempId)} sx={{ p: 0.2, color: '#E53E3E' }}>
                      <Typography sx={{ fontSize: 10 }}>Retry</Typography>
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          )}
        </Box>
      </Box>

      <DeleteMessageDialog
        open={showDeleteDialog}
        message={msg}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={() => handleDeleteConfirm(msg)}
      />
    </>
  );
});

const VoiceRecorder = memo(({ onSend, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    startRecording();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  const getSupportedMimeType = () => {
    if (MediaRecorder.isTypeSupported('audio/ogg; codecs=opus')) {
      return 'audio/ogg; codecs=opus';
    } else if (MediaRecorder.isTypeSupported('audio/webm; codecs=opus')) {
      return 'audio/webm; codecs=opus';
    }
    return null;
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = getSupportedMimeType();
      
      if (!mimeType) {
        throw new Error('Browser does not support audio recording');
      }

      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
      const audioChunks = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) audioChunks.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: mimeType });
        setRecordedBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      startTimeRef.current = Date.now();
      
      timerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        setRecordingTime(elapsed);
      }, 100);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      onCancel();
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  const handleSend = async () => {
    if (recordedBlob) {
      const audioFile = new File([recordedBlob], `voice_${Date.now()}.ogg`, {
        type: 'audio/ogg',
      });
      audioFile.duration = recordingTime;
      onSend(audioFile);
    }
  };

  const handleCancel = () => {
    if (isRecording) stopRecording();
    onCancel();
  };

  return (
    <Box sx={{ 
      flex: 1, 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1, 
      backgroundColor: '#F0F2F5', 
      px: 1,
      py: 0.5
    }}>
      <IconButton 
        onClick={handleCancel}
        sx={{ 
          color: '#54656F', 
          width: 40, 
          height: 40,
          '&:hover': { backgroundColor: 'rgba(84, 101, 111, 0.1)' }
        }}
      >
        <Close />
      </IconButton>

      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        alignItems: 'center', 
        backgroundColor: '#FFFFFF', 
        borderRadius: '24px', 
        px: 2, 
        py: 1,
        boxShadow: '0 1px 3px rgba(11, 20, 26, 0.08)',
        height: '40px'
      }}>
        <Box sx={{ 
          width: 8, 
          height: 8, 
          borderRadius: '50%', 
          backgroundColor: isRecording ? '#E53E3E' : '#00A884',
          animation: isRecording ? 'pulse 1.5s ease-in-out infinite' : 'none',
          '@keyframes pulse': {
            '0%': { opacity: 1, transform: 'scale(1)' },
            '50%': { opacity: 0.5, transform: 'scale(1.2)' },
            '100%': { opacity: 1, transform: 'scale(1)' }
          }
        }} />
        <Typography sx={{ 
          fontSize: '14px', 
          color: isRecording ? '#E53E3E' : '#00A884', 
          fontWeight: 500,
          fontFamily: 'monospace',
          ml: 1,
          flex: 1
        }}>
          {formatTime(recordingTime)}
        </Typography>
        <Typography sx={{ 
          fontSize: '12px', 
          color: '#8696A0', 
          fontWeight: 400
        }}>
          {isRecording ? 'Recording...' : 'Tap to record again'}
        </Typography>
      </Box>

      {!isRecording && recordedBlob ? (
        <IconButton 
          onClick={handleSend}
          sx={{ 
            backgroundColor: '#00A884', 
            color: '#FFFFFF', 
            width: 40, 
            height: 40,
            '&:hover': { backgroundColor: '#008F72' }
          }}
        >
          <Send />
        </IconButton>
      ) : isRecording ? (
        <IconButton 
          onClick={stopRecording}
          sx={{ 
            backgroundColor: '#E53E3E', 
            color: '#FFFFFF', 
            width: 40, 
            height: 40,
            '&:hover': { backgroundColor: '#DC2626' }
          }}
        >
          <Stop />
        </IconButton>
      ) : null}
    </Box>
  );
});

const MediaMenu = memo(({ anchorEl, onClose, onSelect }) => {
  const fileInputRef = useRef(null);

  const handleFileSelect = (acceptedTypes) => {
    if (fileInputRef.current) {
      fileInputRef.current.accept = acceptedTypes;
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      onSelect(file);
      event.target.value = '';
    }
    onClose();
  };

  return (
    <>
      <Menu 
        anchorEl={anchorEl} 
        open={Boolean(anchorEl)} 
        onClose={onClose}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(11, 20, 26, 0.26)',
            borderRadius: '8px'
          }
        }}
      >
        <MenuItem onClick={() => handleFileSelect('image/*')} sx={{ py: 1 }}>
          <Image sx={{ mr: 1.5, color: '#8696A0' }} />
          <Typography sx={{ fontSize: '14px', color: '#3B4A54' }}>Photo</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleFileSelect('audio/*')} sx={{ py: 1 }}>
          <MusicNote sx={{ mr: 1.5, color: '#8696A0' }} />
          <Typography sx={{ fontSize: '14px', color: '#3B4A54' }}>Audio</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleFileSelect('video/*')} sx={{ py: 1 }}>
          <Videocam sx={{ mr: 1.5, color: '#8696A0' }} />
          <Typography sx={{ fontSize: '14px', color: '#3B4A54' }}>Video</Typography>
        </MenuItem>
        <MenuItem onClick={() => handleFileSelect('*')} sx={{ py: 1 }}>
          <Description sx={{ mr: 1.5, color: '#8696A0' }} />
          <Typography sx={{ fontSize: '14px', color: '#3B4A54' }}>Document</Typography>
        </MenuItem>
      </Menu>
      <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
    </>
  );
});

const ChatWindow = memo(({ 
  selectedChat, 
  message, 
  onMessageChange, 
  onSendMessage, 
  onKeyPress, 
  onBackClick, 
  onLoadMoreMessages, 
  hasMoreMessages, 
  isLoadingMessages, 
  hotel, 
  isMobile, 
  onMenuClick, 
  onMediaSelect, 
  onRetryMessage, 
  messageStore, 
  onUpdateName, 
  onDeleteMessage, 
}) => {
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [mediaAnchorEl, setMediaAnchorEl] = useState(null);
  const [nameEditOpen, setNameEditOpen] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const prevMessagesLengthRef = useRef(0);


  useEffect(() => {
    if (selectedChat && messagesEndRef.current) {
      const currentMessagesLength = selectedChat.messages?.length || 0;
      const hadNewMessage = currentMessagesLength > prevMessagesLengthRef.current;

      if (hadNewMessage && isAtBottom) {
        const lastMessage = selectedChat.messages?.[selectedChat.messages.length - 1];
        const hasMedia = lastMessage && lastMessage.messageType !== 'text';
        
        if (hasMedia) {
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 300);
        } else {
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
      
      prevMessagesLengthRef.current = currentMessagesLength;
    }
  }, [selectedChat?.messages, isAtBottom]);

  useEffect(() => {
    if (selectedChat) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
        setIsAtBottom(true);
      }, 150); 
    }
  }, [selectedChat?.number]);

  useEffect(() => {
    if (selectedChat?.messages?.length > 0) {
      const currentMessagesLength = selectedChat.messages.length;
      const lastMessage = selectedChat.messages[currentMessagesLength - 1];
      
      if (lastMessage && 
          !lastMessage.isMe && 
          lastMessage.messageType === 'image' && 
          isAtBottom &&
          currentMessagesLength > prevMessagesLengthRef.current) {
        
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 500);
      }
    }
  }, [selectedChat?.messages?.length, isAtBottom]);
  
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
  
    const { scrollTop, scrollHeight, clientHeight } = container;
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
  
    setShowScrollButton(scrollBottom > 100);
    setIsAtBottom(scrollBottom < 20);
  
    if (scrollTop < 100 && hasMoreMessages && !isLoadingMessages) {
      const prevScrollHeight = container.scrollHeight;
  
      const loadMore = async () => {
        if (typeof onLoadMoreMessages === "function") {
          await onLoadMoreMessages();
  
          requestAnimationFrame(() => {
            const newScrollHeight = container.scrollHeight;
            const heightDiff = newScrollHeight - prevScrollHeight;
            container.scrollTop = scrollTop + heightDiff;
          });
        } else {
          console.error("onLoadMoreMessages is not a function");
        }
      };
  
      loadMore();
    }
  }, [hasMoreMessages, isLoadingMessages, onLoadMoreMessages]);
    
  
  const handleMediaClick = (event) => {
    setMediaAnchorEl(event.currentTarget);
  };
  
  const handleMediaSelect = (file) => {
    const validation = validateFile(file);
    if (!validation.isValid) {
      errorToster(validation.error);
      return;
    }
    onMediaSelect(file);
    setMediaAnchorEl(null);
  };
  
  const handleVoiceSend = async (audioFile) => {
    try {
      await onSendMessage('', audioFile);
      setShowVoiceRecorder(false);
    } catch (error) {
      errorToster('Failed to send voice message');
    }
  };

  const getLastMessageTime = () => {
    if (!selectedChat?.messages?.length) return '';
  
    const lastMsg = selectedChat.messages[selectedChat.messages.length - 1];
    const timestamp = lastMsg.timestamp;
  
    if (!timestamp) return '';
  
    const messageDate = new Date(timestamp);
  
    if (isToday(messageDate)) {
      return lastMsg.time;
    }
  
    if (isYesterday(messageDate)) {
      return 'Yesterday';
    }
  
    return `${format(messageDate, 'MMM d')}`;
  };  
  
  const handleDeleteMessage = (msg) => {
    onDeleteMessage && onDeleteMessage(msg);
  };
  
  const groupMessagesByDate = (messages) => {
    const grouped = [];
    let currentDate = null;
    let unreadShown = false;
    
    messages.forEach((msg, index) => {
      const msgDate = new Date(msg.timestamp);
      const dateStr = msgDate.toDateString();
      
      if (!unreadShown && !msg.isMe && selectedChat?.unreadCount > 0) {
        const unreadIndex = messages.findIndex(m => !m.isMe && m.timestamp > (Date.now() - 24 * 60 * 60 * 1000));
        if (index === unreadIndex) {
          grouped.push({ type: 'unread', id: `unread-${index}` });
          unreadShown = true;
        }
      }
      
      if (currentDate !== dateStr) {
        currentDate = dateStr;
        grouped.push({ type: 'date', date: msg.timestamp, id: `date-${dateStr}` });
      }
      
      grouped.push({ type: 'message', message: msg, id: msg.id || msg.tempId });
    });
    
    return grouped;
  };
  
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, []);
  
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && selectedChat) {
        if (Notification.permission === 'default') {
          Notification.requestPermission();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedChat]);
  
  if (!selectedChat) {
    return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F0F2F5',
            flexDirection: 'column',
            textAlign: 'center',
            gap: 2,
          }}
        >
          <Chat sx={{ fontSize: 80, color: '#A0A0A0' }} />
          <Typography variant="h6" color="textSecondary">
            Select a chat to start messaging
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Your messages will appear here
          </Typography>
        </Box>
      );
  }
  
  const groupedMessages = groupMessagesByDate(selectedChat.messages || []);
  
  return (
    <Box sx={{ 
      flex: 1, 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      backgroundColor: '#EFEAE2',
      backgroundImage: 'url("data:image/svg+xml,%3Csvg width="260" height="260" viewBox="0 0 260 260" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23002" fill-opacity="0.02"%3E%3Cpath d="M24.37 16c.2.65.39 1.32.54 2H21.17l1.17-2h2.03zm-2.36-2h1.8c.1-.32.17-.66.22-1H20.7l1.31 1zM20.7 12H24c.17-.66.39-1.32.54-2H21.17l-.47 2zm2.36-2h2.03c.2-.65.39-1.32.54-2H21.17l1.53 2zm-2.36-2h1.8c.1-.32.17-.66.22-1H20.7l1.31 1zM20.7 4H24c.17-.66.39-1.32.54-2H21.17l-.47 2zm2.36-2h2.03c.2-.65.39-1.32.54-2H21.17l1.53 2z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
      position: 'relative'
    }}>
      {/* Header */}
      <Box sx={{ 
        p: { xs: 1, sm: 2 }, 
        backgroundColor: '#F0F2F5', 
        borderLeft: '1px solid #E4E7EA', 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        minHeight: 70 
      }}>
        {isMobile && (
          <IconButton onClick={onBackClick} sx={{ color: '#54656F' }}>
            <ArrowBack />
          </IconButton>
        )}
        <Avatar sx={{ width: 40, height: 40, backgroundColor: '#00A884' }}>
          <Person />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 500, 
              color: '#111B21', 
              cursor: 'pointer',
            }}
            onClick={() => setNameEditOpen(true)}
          >
            {selectedChat.name}
            <IconButton>
                <Edit sx={{ width: 15, height: 15}} />
            </IconButton>
          </Typography>
          <Typography variant="body2" sx={{ color: '#667781', fontSize: '13px' }}>
            {selectedChat.isBlocked ? 'Blocked' : `Last message at ${getLastMessageTime()}`}
          </Typography>
        </Box>
        <IconButton onClick={onMenuClick} sx={{ color: '#54656F' }}>
          <MoreVert />
        </IconButton>
      </Box>
  
      {/* Messages */}
      <Box 
        ref={messagesContainerRef} 
        className="messages-container" 
        onScroll={handleScroll} 
        sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          px: { xs: 1, sm: 2 }, 
          pb: 1,
          '&::-webkit-scrollbar': { width: '6px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': { 
            background: 'rgba(11, 20, 26, 0.2)', 
            borderRadius: '3px',
            '&:hover': { background: 'rgba(11, 20, 26, 0.3)' }
          }
        }}
      >
        {/* Loading indicator at top when loading more messages */}
        {isLoadingMessages && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <CircularProgress size={20} sx={{ color: '#00A884' }} />
          </Box>
        )}

        {/* Messages */}
        {groupedMessages.map((item) => {
          if (item.type === 'date') {
            return <DateHeader key={item.id} date={item.date} />;
          }
          if (item.type === 'unread') {
            return <DateHeader key={item.id} date={null} isUnread />;
          }
          const msg = item.message;
          const showRetry = msg.status === 'failed' && messageStore.has(msg.tempId);
          return (
            <MessageBubble 
              key={item.id} 
              msg={msg} 
              onRetry={onRetryMessage} 
              showRetry={showRetry} 
              onDelete={handleDeleteMessage} 
            />
          );
        })}
        
        <div ref={messagesEndRef} />
      </Box>
  
      {/* Input Area */}
      <Box sx={{ 
        p: { xs: 1, sm: 1.5 }, 
        backgroundColor: '#F0F2F5', 
        borderTop: '1px solid #E4E7EA' 
      }}>
        {showVoiceRecorder ? (
          <VoiceRecorder
            onSend={handleVoiceSend}
            onCancel={() => setShowVoiceRecorder(false)}
          />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <IconButton
              onClick={handleMediaClick}
              sx={{ 
                color: '#54656F', 
                width: 40, 
                height: 40,
                '&:hover': { backgroundColor: 'rgba(84, 101, 111, 0.1)' }
              }}
            >
              <AttachFile />
            </IconButton>
            
            <TextField
              multiline
              maxRows={4}
              value={message}
              onChange={(e) => onMessageChange(e.target.value)}
              onKeyPress={onKeyPress}
              placeholder="Type a message"
              variant="outlined"
              size="small"
              sx={{ 
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '15px',
                  backgroundColor: 'white',
                  '& fieldset': { border: 'none' },
                  boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                  py: 0.5,
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {/* <IconButton size="small" sx={{ color: '#8696A0' }}>
                      <EmojiEmotions />
                    </IconButton> */}
                  </InputAdornment>
                )
              }}
            />
            
            {message.trim() ? (
              <IconButton
                onClick={() => onSendMessage()}
                sx={{ 
                  backgroundColor: '#00A884', 
                  color: '#FFFFFF', 
                  width: 40, 
                  height: 40,
                  '&:hover': { backgroundColor: '#008F72' }
                }}
              >
                <Send />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => setShowVoiceRecorder(true)}
                sx={{ 
                  backgroundColor: '#00A884', 
                  color: '#FFFFFF', 
                  width: 40, 
                  height: 40,
                  '&:hover': { backgroundColor: '#008F72' }
                }}
              >
                <Mic />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
  
      {/* Scroll to Bottom Button */}
      {showScrollButton && (
        <Box sx={{ 
          position: 'absolute', 
          bottom: 80, 
          right: 20, 
          zIndex: 1000 
        }}>
          <IconButton
            onClick={scrollToBottom}
            sx={{ 
              backgroundColor: '#00A884', 
              color: '#FFFFFF', 
              width: 40, 
              height: 40,
              boxShadow: '0 2px 10px rgba(11, 20, 26, 0.2)',
              '&:hover': { backgroundColor: '#008F72' }
            }}
          >
            <KeyboardArrowDown />
          </IconButton>
        </Box>
      )}
  
      {/* Media Menu */}
      <MediaMenu
        anchorEl={mediaAnchorEl}
        onClose={() => setMediaAnchorEl(null)}
        onSelect={handleMediaSelect}
      />
  
      {/* Name Edit Dialog */}
      <NameEditDialog
        open={nameEditOpen}
        currentName={selectedChat.name}
        onClose={() => setNameEditOpen(false)}
        onSave={(newName) => {
          onUpdateName && onUpdateName(newName);
          setNameEditOpen(false);
        }}
      />
    </Box>
  );
  });
  
  export default ChatWindow;